import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { fmt } from '../utils/format';

function formatCloseReason(reason: string | null | undefined): string {
  if (!reason) return 'N/A';
  return reason
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  try {
    const d = iso.endsWith('Z') || iso.includes('+') ? new Date(iso) : new Date(iso + 'Z');
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
  } catch {
    return 'N/A';
  }
}

export interface SettledTrade {
  time: string;
  ticker: string;
  realized_pnl: number;
  cumulative_pnl: number;
  strategy_id: number | null;
  trade_source?: string;
  market_title?: string | null;
  side?: string | null;
  entry_price?: number | null;
  exit_price?: number | null;
  quantity?: number | null;
  close_reason?: string | null;
  entry_time?: string | null;
}

export interface OpenPositionDetail {
  ticker: string;
  entry_time: string | null;
  cost: number;
  unrealized_pnl: number;
  strategy_id: number | null;
  trade_source: string;
  market_title?: string | null;
  side?: string | null;
  entry_price?: number | null;
  quantity?: number | null;
}

interface TradeDetailDialogProps {
  /** A settled trade to show detail for (mutually exclusive with showNow) */
  trade: SettledTrade | null;
  /** Whether to show the "Now" open positions summary */
  showNow: boolean;
  /** Open positions data for "Now" mode */
  openPositions: OpenPositionDetail[];
  /** Total unrealized P&L */
  totalUnrealized: number;
  /** Strategy name lookup */
  getStrategyName: (id: number | null) => string;
  onClose: () => void;
}

export default function TradeDetailDialog({
  trade,
  showNow,
  openPositions,
  totalUnrealized,
  getStrategyName,
  onClose,
}: TradeDetailDialogProps) {
  const open = !!trade || showNow;

  if (!open) return null;

  // Settled trade mode
  if (trade) {
    const pnl = trade.realized_pnl || 0;
    const items = [
      { label: 'Market', value: trade.market_title || trade.ticker },
      { label: 'Ticker', value: trade.ticker },
      { label: 'Strategy', value: getStrategyName(trade.strategy_id) },
      { label: 'Side', value: trade.side ? trade.side.toUpperCase() : 'N/A' },
      { label: 'Entry Price', value: trade.entry_price != null ? `$${fmt(trade.entry_price)}` : 'N/A' },
      { label: 'Exit Price', value: trade.exit_price != null ? `$${fmt(trade.exit_price)}` : 'N/A' },
      { label: 'Quantity', value: trade.quantity != null ? trade.quantity : 'N/A' },
      {
        label: 'P&L',
        value: `${pnl >= 0 ? '+' : ''}$${fmt(pnl)}`,
        color: pnl >= 0 ? '#27AE60' : '#C0392B',
      },
      { label: 'Close Reason', value: formatCloseReason(trade.close_reason) },
      { label: 'Entry Time', value: formatTime(trade.entry_time) },
      { label: 'Exit Time', value: formatTime(trade.time) },
      { label: 'Source', value: trade.trade_source === 'manual' ? 'Manual' : 'Autonomous' },
    ];

    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { backgroundColor: '#FDF6EC', border: '1px solid #F0E4CE' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {trade.market_title || trade.ticker}
          {trade.market_title && (
            <Typography variant="caption" sx={{ color: '#7A5F4E', display: 'block' }}>
              {trade.ticker}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {items.map((item) => (
              <Grid item xs={6} key={item.label}>
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  color: (item as any).color || 'inherit',
                }}>
                  {item.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // "Now" open positions mode
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: '#FDF6EC', border: '1px solid #F0E4CE' } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Open Positions ({openPositions.length})
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            Total Unrealized P&L:&nbsp;
            <span style={{
              color: totalUnrealized >= 0 ? '#27AE60' : '#C0392B',
              fontWeight: 700,
            }}>
              {totalUnrealized >= 0 ? '+' : ''}${fmt(totalUnrealized)}
            </span>
          </Typography>
        </Box>
        {openPositions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No open positions.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Market</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Strategy</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Side</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Entry</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>P&L</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openPositions.map((pos, i) => {
                  const pnl = pos.unrealized_pnl;
                  return (
                    <TableRow key={`${pos.ticker}-${i}`}>
                      <TableCell sx={{ fontSize: 13, maxWidth: 200 }}>
                        <Typography variant="body2" noWrap>
                          {pos.market_title || pos.ticker}
                        </Typography>
                        {pos.market_title && (
                          <Typography variant="caption" noWrap sx={{ color: '#7A5F4E', display: 'block', fontSize: '0.65rem' }}>
                            {pos.ticker}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {getStrategyName(pos.strategy_id)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {pos.side ? pos.side.toUpperCase() : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>
                        {pos.entry_price != null ? `$${fmt(pos.entry_price)}` : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>
                        {pos.quantity ?? 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: pnl >= 0 ? '#27AE60' : '#C0392B',
                      }}>
                        {pnl >= 0 ? '+' : ''}${fmt(pnl)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
